import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, Typography } from '@mui/material';
import { authApi } from '../../services/api';
import { loadGoogleIdentity, onGoogleCredential } from '../../services/googleIdentity';
import { useAuthStore } from '../../store/authStore';
import { KeyConfirmation } from '../../types';
import { errorMessage } from '../../utils/errors';

/** The first message out of a `{confirm: [message]}` answer, which is how a refused confirmation is reported. */
function refusal(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: Record<string, unknown> } })?.response?.data;
  for (const field of ['confirm', 'detail']) {
    const value = data?.[field];
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    if (typeof value === 'string') return value;
  }
  return errorMessage(e, fallback);
}

/**
 * Deleting the account, from the bottom of the Profile page. The person
 * proves again who they are (a fresh Google sign-in for the same address,
 * or their password if the account has one); everything is removed at once.
 */
const DeleteAccount: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const signOutLocally = useAuthStore((state) => state.logout);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const googleSlot = useRef<HTMLDivElement | null>(null);

  const remove = useCallback(
    async (confirmation: KeyConfirmation) => {
      setWorking(true);
      setProblem(null);
      try {
        await authApi.deleteAccount(confirmation);
        // The account is gone; the tokens are dead. Clear this browser and say goodbye.
        await signOutLocally();
        navigate('/login', { replace: true, state: { message: 'Your account and its data have been deleted.' } });
      } catch (e) {
        setProblem(refusal(e, 'The account could not be deleted.'));
        setWorking(false);
      }
    },
    [navigate, signOutLocally]
  );

  const removeRef = useRef(remove);
  removeRef.current = remove;
  useEffect(() => {
    if (!open) return undefined;
    const release = onGoogleCredential((credential) => removeRef.current({ google_credential: credential }));
    let cancelled = false;
    loadGoogleIdentity()
      .then((googleId) => {
        const slot = googleSlot.current;
        if (cancelled || !slot) return;
        slot.textContent = '';
        googleId.renderButton(slot, { theme: 'outline', size: 'large', text: 'continue_with', width: 280 });
      })
      .catch(() => {
        // No Google button (blocked or offline): the password box still works for accounts that have one.
      });
    return () => {
      cancelled = true;
      release();
    };
  }, [open]);

  if (!user) return null;

  return (
    <Card variant="outlined" component="section" aria-labelledby="delete-account-title" sx={{ mt: 3, borderColor: 'error.light' }}>
      <CardContent>
        <Typography id="delete-account-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          Delete account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Removes your profile, answers, resumes, Experience library, tailored resumes and their files, applications, email tracking and saved
          searches, all at once. This cannot be undone.
        </Typography>
        <Button color="error" variant="outlined" onClick={() => setOpen(true)}>
          Delete my account
        </Button>
      </CardContent>

      <Dialog open={open} onClose={working ? undefined : () => setOpen(false)} fullWidth maxWidth="xs" aria-labelledby="delete-account-dialog-title">
        <DialogTitle id="delete-account-dialog-title">Delete {user.email}?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {problem && <Alert severity="error">{problem}</Alert>}
            <Typography variant="body2">
              Everything in this account is deleted now and cannot be brought back. If you only want a break, sign out instead.
            </Typography>
            <Divider />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Confirm it is you
            </Typography>
            <Box ref={googleSlot} sx={{ minHeight: 44, ...(working && { pointerEvents: 'none', opacity: 0.6 }) }} />
            <TextField
              label="Or your password, if the account has one"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && password) remove({ password });
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={working}>
            Cancel
          </Button>
          <Button color="error" variant="contained" disabled={working || !password} onClick={() => remove({ password })}>
            {working ? 'Deleting...' : 'Delete everything'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default DeleteAccount;
